import { Form, Formik, useFormikContext } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { t } from '@nodal/i18n';
import { TextInput } from '@nodal/uikit/components/TextInput';
import { SurrogateContactDetailsProps } from './SurrogateContactDetails.interface';
import { PastDateInput } from '@nodal/uikit/components/DateInput';
import { PhoneNumberInput } from '@nodal/uikit/components/PhoneNumberInput';
import { ButtonLoading } from '@nodal/uikit/components/ButtonLoading';
import { Address } from 'models';
import { AddressInput } from '@nodal/uikit/components/AddressInput';
import { Checkbox } from '@nodal/uikit/components/Checkbox';
import { MailingAddressInput } from '@nodal/uikit/components/MailingAddressInput';

const getFieldErrorNames = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formikErrors: any,
): string[] => {
  const transformObjectToDotNotation = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    obj: any,
    prefix = '',
    result: string[] = [],
  ): string[] => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (!value) return;

      const nextKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'object') {
        transformObjectToDotNotation(value, nextKey, result);
      } else {
        result.push(nextKey);
      }
    });

    return result;
  };

  return transformObjectToDotNotation(formikErrors);
};

const ScrollToFieldError = () => {
  const { submitCount, errors } = useFormikContext();
  const lastProcessedSubmitRef = useRef<number>(0);
  const pendingSubmitRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (submitCount > 0 && submitCount !== lastProcessedSubmitRef.current) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }

      pendingSubmitRef.current = submitCount;
      lastProcessedSubmitRef.current = submitCount;
    }
  }, [submitCount]);

  useEffect(() => {
    if (pendingSubmitRef.current === null || submitCount === 0) {
      return;
    }

    const fieldErrorNames = getFieldErrorNames(errors);

    if (fieldErrorNames.length === 0) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }

    const checkAndScroll = (attempt = 0) => {
      const currentFieldErrorNames = getFieldErrorNames(errors);

      if (currentFieldErrorNames.length === 0 && attempt < 10) {
        const delay =
          attempt < 3 ? 200 : attempt < 6 ? 300 : attempt < 8 ? 400 : 500;
        scrollTimeoutRef.current = setTimeout(() => {
          checkAndScroll(attempt + 1);
        }, delay);
        return;
      }

      if (currentFieldErrorNames.length === 0) {
        pendingSubmitRef.current = null;
        return;
      }

      const currentFirstErrorField = currentFieldErrorNames[0];
      const currentBaseName = currentFirstErrorField.includes('.')
        ? currentFirstErrorField.split('.')[0]
        : currentFirstErrorField;

      let inputElement: HTMLElement | null = null;

      if (currentFirstErrorField.includes('.')) {
        inputElement = document.querySelector(
          `input[name^='${currentBaseName}.']`,
        ) as HTMLElement;
      }

      if (!inputElement) {
        inputElement =
          document.querySelector(`input[name='${currentFirstErrorField}']`) ||
          document.querySelector(
            `textarea[name='${currentFirstErrorField}']`,
          ) ||
          document.querySelector(`select[name='${currentFirstErrorField}']`) ||
          null;
      }

      if (!inputElement) {
        if (attempt < 5) {
          scrollTimeoutRef.current = setTimeout(() => {
            checkAndScroll(attempt + 1);
          }, 300);
        } else {
          pendingSubmitRef.current = null;
        }
        return;
      }

      const container =
        inputElement.closest('.bg-white.rounded-lg') ||
        inputElement.closest('.p-8') ||
        inputElement.parentElement;

      if (!container) {
        if (attempt < 5) {
          scrollTimeoutRef.current = setTimeout(() => {
            checkAndScroll(attempt + 1);
          }, 300);
        } else {
          pendingSubmitRef.current = null;
        }
        return;
      }

      const scrollToContainer = () => {
        let element: HTMLElement | null = container as HTMLElement;
        let offsetTop = 0;

        while (element) {
          offsetTop += element.offsetTop;
          element = element.offsetParent as HTMLElement | null;
        }

        const containerHeight = (container as HTMLElement).offsetHeight;
        const viewportHeight = window.innerHeight;

        let targetScrollY: number;
        if (containerHeight < viewportHeight) {
          const headerHeight = 80;
          targetScrollY = offsetTop - headerHeight - 20;
        } else {
          targetScrollY = offsetTop + containerHeight / 2 - viewportHeight / 2;
        }

        window.scrollTo({
          top: Math.max(0, targetScrollY),
          behavior: 'smooth',
        });
      };

      scrollToContainer();
      setTimeout(scrollToContainer, 50);
      setTimeout(scrollToContainer, 200);

      focusTimeoutRef.current = setTimeout(() => {
        focusTimeoutRef.current = null;
        if (
          inputElement &&
          (inputElement instanceof HTMLInputElement ||
            inputElement instanceof HTMLTextAreaElement ||
            inputElement instanceof HTMLSelectElement)
        ) {
          inputElement.focus();
        }
        pendingSubmitRef.current = null;
      }, 400);
    };

    scrollTimeoutRef.current = setTimeout(() => {
      scrollTimeoutRef.current = null;
      checkAndScroll();
    }, 800);
  }, [submitCount, errors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

export const SurrogateContactDetails: FC<SurrogateContactDetailsProps> = ({
  initialValues,
  onSubmit,
  validationSchema,
  states,
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={onSubmit}
    validationSchema={validationSchema}
    validateOnBlur={false}
    validationOnChange={false}
  >
    {({ values, isSubmitting, setFieldValue, setFieldTouched }) => (
      <Form className="space-y-4 w-full lg:w-step-view">
        <ScrollToFieldError />
        <div className="p-8 bg-white rounded-lg">
          <h3 className="text-xl font-semibold leading-7 text-gray-900">
            {t('Personal Information')}
          </h3>
          <div className="grid grid-cols-1 gap-x-4 gap-y-10 mt-6">
            <TextInput label={t('First Name')} name="first_name" type="text" />
            <TextInput
              label={t('Middle Name')}
              name="middle_name"
              type="text"
            />
            <TextInput label={t('Last Name')} name="last_name" type="text" />
            <TextInput
              label={t(
                'Maiden name or any other names used in the past (optional)',
              )}
              name="maiden_name"
              type="text"
            />
            <TextInput
              label={t('Preferred name (optional)')}
              name="preferred_name"
              type="text"
            />
            <PastDateInput
              label={t('Date of Birth')}
              name="date_of_birth"
              placeholder="YYYY-DD-MM"
              className="lg:w-1/2"
            />
          </div>
        </div>

        <div className="p-8 bg-white rounded-lg">
          <div>
            <h3 className="text-xl font-semibold leading-7 text-gray-900">
              {t('Contact Information')}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-10 mt-6">
            <PhoneNumberInput
              label={t('Phone Number')}
              name="phone_number"
              placeholder="(555) 987-6543"
              type="text"
              className="lg:w-1/2"
            />
            <AddressInput
              name="address"
              onChange={(address: Address) =>
                Object.entries(address).forEach(([key, value]) => {
                  setFieldValue(
                    `address.${key}`,
                    key === 'state' &&
                      states.find((s) => s.value === value)?.disabled
                      ? null
                      : value,
                  );
                  setFieldTouched(`address.${key}`, false);
                })
              }
              states={states}
            />
            <div className="mt-3">
              <Checkbox
                name="same_mailing_address"
                label={'This is also my mailing address'}
              />
            </div>
            {!values.same_mailing_address && (
              <MailingAddressInput
                name="mailing_address"
                onChange={(address: Address) =>
                  Object.entries(address).forEach(([key, value]) => {
                    setFieldValue(`mailing_address.${key}`, value);
                    setFieldTouched(`mailing_address.${key}`, false);
                  })
                }
                states={states}
              />
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <ButtonLoading
            type="submit"
            variant="primary"
            loading={isSubmitting}
            className="justify-center w-full sm:w-max"
          >
            {t('Submit')}
          </ButtonLoading>
        </div>
      </Form>
    )}
  </Formik>
);
